// opervex-frontend/src/components/FilterDialog.js

import React, { useState } from "react";
import { Modal, Button, Form, InputGroup, Col, Row } from "react-bootstrap";
import { FaTrash } from "react-icons/fa";
import { FiFilter } from "react-icons/fi";

import CurrencyInput from "../components/CurrencyInput";
import useOpervex from "../hooks/useOpervex";
import { LOGIC_OPERATORS, FILTER_OPTIONS } from "../constants/filters";

const FilterDialog = ({ layout, setData, urlData, setPage }) => {
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState([]);

  const OpervexAPI = useOpervex();

  const visibleFields = layout.filter((col) => col.visible && !col.hidden);

  const handleShow = () => {
    setShow(true);
  };

  const handleClose = () => {
    setShow(false);
  };

  const handleAddFilter = () => {
    const item = visibleFields[0];

    if (!item) return;

    const options = FILTER_OPTIONS[item.type] || [];
    const defaultOperator = options[0]?.value || "=";

    setFilters((prev) => [
      ...prev,
      {
        fieldname: item.key,
        operator: defaultOperator,
        value: "",
        logic: prev.length ? "AND" : "",
        type: item.type,
      },
    ]);
  };

  const handleRemoveFilter = (index) => {
    setFilters((prev) =>
      prev
        .filter((_, i) => i !== index)
        .map((filter, i) => ({
          ...filter,
          logic: i === 0 ? "" : filter.logic || "AND",
        })),
    );
  };

  const handleChangeFilter = (index, key, value) => {
    setFilters((prev) =>
      prev.map((filter, i) => {
        if (i !== index) return filter;

        if (key === "fieldname") {
          const item = layout.find((col) => col.key === value);

          if (!item) {
            return {
              ...filter,
              fieldname: value,
            };
          }

          const options = FILTER_OPTIONS[item.type] || [];

          return {
            ...filter,
            fieldname: value,
            type: item.type,
            operator: options[0]?.value || "=",
            value: "",
          };
        }

        return {
          ...filter,
          [key]: value,
        };
      }),
    );
  };

  const handleFiltrar = async () => {
    setLoading(true);

    const filterBody = filters.map((filter, index) => ({
      operation: index === 0 ? "AND" : filter.logic,
      field: filter.fieldname,
      condition: filter.operator,
      value: filter.value,
    }));

    try {
      const result = await OpervexAPI.Axios.post(urlData, filterBody);

      setData(result);
      setPage(0);
      handleClose();
    } catch (err) {
      console.error("Erro ao carregar os dados:", err);
    } finally {
      setLoading(false);
    }
  };

  const renderFilterInput = (filter, index) => {
    switch (filter.type) {
      case "boolean":
        return (
          <Form.Select value={filter.value} onChange={(e) => handleChangeFilter(index, "value", e.target.value)} size="sm">
            <option value="">Selecione</option>
            <option value="true">Verdadeiro</option>
            <option value="false">Falso</option>
          </Form.Select>
        );

      case "currency":
        return <CurrencyInput value={filter.value} onChange={(e) => handleChangeFilter(index, "value", e.target.value)} />;

      case "date":
      case "dateOnly":
        return (
          <Form.Control
            type="date"
            value={filter.value}
            onChange={(e) => handleChangeFilter(index, "value", e.target.value)}
            size="sm"
          />
        );

      case "number":
      case "integer":
        return (
          <Form.Control
            type="number"
            step="1"
            value={filter.value}
            onChange={(e) => handleChangeFilter(index, "value", e.target.value)}
            size="sm"
          />
        );

      case "decimal":
        return (
          <Form.Control
            type="number"
            step="any"
            value={filter.value}
            onChange={(e) => handleChangeFilter(index, "value", e.target.value)}
            size="sm"
          />
        );

      default:
        return (
          <Form.Control
            type="text"
            value={filter.value}
            onChange={(e) => handleChangeFilter(index, "value", e.target.value)}
            size="sm"
          />
        );
    }
  };

  return (
    <>
      <Button variant="primary" size="sm" onClick={handleShow}>
        <FiFilter /> Filtros
      </Button>

      <Modal show={show} onHide={handleClose} size="lg" backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>Filtros</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {filters.map((filter, index) => {
            const operatorOptions = FILTER_OPTIONS[filter.type] || [];

            return (
              <Row className="mb-1" key={index}>
                <InputGroup className="p-0" size="sm">
                  <Col sm="1" className="px-1">
                    {index > 0 && (
                      <Form.Select
                        value={filter.logic}
                        onChange={(e) => handleChangeFilter(index, "logic", e.target.value)}
                        size="sm"
                      >
                        {LOGIC_OPERATORS.map((op) => (
                          <option key={op.value} value={op.value}>
                            {op.label}
                          </option>
                        ))}
                      </Form.Select>
                    )}
                  </Col>

                  <Col sm="3" className="px-1">
                    <Form.Select
                      value={filter.fieldname}
                      onChange={(e) => handleChangeFilter(index, "fieldname", e.target.value)}
                      size="sm"
                    >
                      {visibleFields.map((col) => (
                        <option key={col.key} value={col.key}>
                          {col.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Col>

                  <Col sm="2" className="px-1">
                    <Form.Select
                      value={filter.operator}
                      onChange={(e) => handleChangeFilter(index, "operator", e.target.value)}
                      size="sm"
                    >
                      {operatorOptions.map((op) => (
                        <option key={op.value} value={op.value}>
                          {op.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Col>

                  <Col sm="5" className="px-1">
                    {renderFilterInput(filter, index)}
                  </Col>

                  <Col sm="1" className="px-1">
                    <Button variant="outline-danger" size="sm" onClick={() => handleRemoveFilter(index)}>
                      <FaTrash />
                    </Button>
                  </Col>
                </InputGroup>
              </Row>
            );
          })}

          <br />

          <Button variant="outline-success" onClick={handleAddFilter} disabled={!visibleFields.length}>
            Adicionar Filtro
          </Button>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="primary" onClick={handleFiltrar} disabled={loading}>
            {loading ? "Carregando..." : "Filtrar"}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default FilterDialog;
